/**
 * Database Explorer — the raw SQL runner contract.
 *
 * `sql.run` is the SQL Editor's and the Assistant's path into the single
 * gated runner (packages/core/src/db-explorer.ts → runSql). The same
 * RunResult shape is reused by rows.* / ddl.* write operations.
 */

import { z } from "zod";
import type { Operation } from "../../types";

const stmtKind = z.enum(["read", "write", "ddl", "unknown"]);

export const statementResultSchema = z.object({
  sql: z.string(),
  kind: stmtKind,
  columns: z.array(z.string()).optional(),
  rows: z.array(z.array(z.unknown())).optional(),
  rowCount: z.number().optional(),
  truncated: z.boolean().optional(),
  changes: z.number().optional(),
  lastInsertRowid: z.number().optional(),
  error: z.string().optional(),
});
export type StatementResultDto = z.infer<typeof statementResultSchema>;

export const runResultSchema = z.object({
  statements: z.array(statementResultSchema),
  elapsedMs: z.number(),
  readOnly: z.boolean(),
});
export type RunResultDto = z.infer<typeof runResultSchema>;

export const sqlRunBody = z.object({
  sql: z.string().min(1),
  readOnly: z.boolean().optional(),
  confirmDestructive: z.boolean().optional(),
});
export type SqlRunBody = z.infer<typeof sqlRunBody>;

export const sqlRun: Operation<
  void,
  void,
  z.infer<typeof sqlRunBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/sql",
  body: sqlRunBody,
  response: runResultSchema,
};

export const sql = { run: sqlRun };
